/**
 * Area API Methods
 *
 * Provides functions for querying location/area information from the HotPads API.
 * All requests are routed through the /hotpads-api/api proxy to bypass PerimeterX.
 *
 * Note on paths:
 * - Paths are relative to the API proxy base (/hotpads-api/api)
 * - Client/SSR requests go to: /hotpads-api/api/v2/area/...
 * - Next.js rewrites to: /api/hotpads-api/v2/area/...
 * - Proxy forwards to: https://hotpads-api.hotpads.com/hotpads-api/api/v2/area/...
 */

import { HpApiWebApiError, HpSearchScopeType } from '@zg-rentals/ts-hotpads-api-client';
import type {
  HpApiResponseDto,
  HpAreaArticleDto,
  HpSearchScopeDto,
} from '@zg-rentals/ts-hotpads-api-client';

import { toSearchScope } from '@/src/lib/filters/searchScope';

import { hasUsableEncodedBoundary } from '../../map/boundaryAvailability';
import { type GeoJsonBoundaryFeature, geoJsonFeatureToEncodedBoundary } from '../../map/geoJsonBoundary';
import { apiClient, ApiError, type ApiServerRouting, isRequestTimeoutError } from '../client/apiClient';

type AreaRequestOptions = {
  timeoutMs?: number;
};

type AreaByIpOptions = AreaRequestOptions & {
  headers?: Record<string, string>;
  proxyOrigin?: string;
  serverRouting?: ApiServerRouting;
};

export interface AreaResponse {
  /** Legacy Place Area id when the response has a real mapping. */
  id?: string;
  /** Explicit alias for the optional legacy Place Area id. */
  legacyAreaId?: string;
  scope?: HpSearchScopeDto;
  resourceId?: string;
  name?: string;
  type?: string;
  uriV2?: string;
  state?: string;
  city?: string;
  zip?: string;
  neighborhood?: string;
  county?: string;
  countryCode?: string;
  minLat?: number;
  maxLat?: number;
  minLon?: number;
  maxLon?: number;
  coordinates?: {
    lon: number;
    lat: number;
  };
  // At most one typed Zillow id is populated per area; omitted when null.
  zillowRegionId?: number;
  zillowSchoolId?: number;
  zillowSchoolDistrictId?: number;
  breadcrumbs?: Array<AreaResponse>;
}

export type AreaByIpResponse = AreaResponse;
export type AreaBestFitResponse = AreaResponse;

/** A resource-resolved area with an optional legacy Area mapping. */
export type AreaBootstrap = Omit<AreaResponse, 'resourceId' | 'scope'> & {
  resourceId: string;
  scope: HpSearchScopeDto;
};

export type AreaByResourceIdResponse = AreaBootstrap;
export type AreaIdentity = Pick<
  AreaResponse,
  'resourceId' | 'scope' | 'legacyAreaId' | 'id' | 'zillowRegionId' | 'zillowSchoolId' | 'zillowSchoolDistrictId'
>;

function withLegacyAreaId<T extends AreaResponse>(area: T): T;
function withLegacyAreaId<T extends AreaResponse>(area: T | null | undefined): T | null;
function withLegacyAreaId<T extends AreaResponse>(area: T | null | undefined): T | null {
  if (!area) return null;
  return { ...area, legacyAreaId: area.legacyAreaId ?? area.id };
}

export function getAreaScopeKey(identity: AreaIdentity): string {
  const scope = toSearchScope(identity);
  if (scope) return `${scope.type}:${scope.id}`;
  return `resource:${identity.resourceId ?? identity.legacyAreaId ?? identity.id ?? ''}`;
}

type AreaByResourceIdEnvelope = {
  data?: AreaByResourceIdResponse | null;
  success?: boolean;
  status?: string;
  message?: string;
};

export interface BoundaryData {
  type?: string;
  // MultiPolygon structure: array of polygons, each containing array of rings (outer + holes)
  // Each ring is an encoded polyline string
  coordinates?: Array<Array<string>>;
}

export interface EncodedBoundaryResponse {
  area?: AreaResponse;
  boundary?: BoundaryData;
  // Mapi CDN GeoJSON url for a region-backed legacy-area response. The
  // browser fetches and re-encodes it when present.
  boundaryGeoJsonUrl?: string;
}

export interface AutocompleteResult {
  type: string;
  subType?: string;
  name: string;
  id?: number;
  resourceId?: string;
  city?: string;
  county?: string;
  state?: string;
  zillowRegionId?: number;
  zillowRegionType?: string;
  lotIdEncoded?: string;
  isNearMe?: boolean;
}

export interface AutocompleteResponse {
  status?: string;
  loggedIn?: boolean;
  verified?: boolean;
  validationStatus?: string;
  success?: boolean;
  data?: Array<AutocompleteResult>;
}

/**
 * Get area information based on IP address
 * @param ip - IP address to lookup
 * @returns Area information for the IP's location
 */
export async function byIp(ip: string, options?: AreaByIpOptions): Promise<AreaByIpResponse | null> {
  const response = await apiClient<{ data: AreaByIpResponse | null }>({
    path: '/v2/area/byIp',
    queryParams: { ip },
    headers: options?.headers,
    proxyOrigin: options?.proxyOrigin,
    serverRouting: options?.serverRouting,
    timeout: options?.timeoutMs,
    // Middleware records its short deadline as a fallback warning, not an exception.
    isExpectedError: isRequestTimeoutError,
  });
  return withLegacyAreaId(response.data);
}

/**
 * Get area information by resource ID
 * @param resourceId - Area resource ID (e.g., "berkeley-ca")
 * @returns Area information and boundary for the resource
 */
export async function byResourceId(
  resourceId: string,
  options?: AreaRequestOptions,
): Promise<AreaByResourceIdResponse | null> {
  const response = await apiClient<AreaByResourceIdEnvelope>({
    path: '/v2/area/byResourceId',
    queryParams: { resourceId },
    ...(options?.timeoutMs === undefined
      ? {}
      : { timeout: options.timeoutMs, isExpectedError: isRequestTimeoutError }),
  });
  if (response?.success === false) {
    if (response.status === 'DATA_NOT_FOUND') return null;
    const error = new ApiError(response.message ?? `Area lookup failed with status ${response.status ?? 'unknown'}`, {
      data: response,
    });
    error.category = 'API_ENVELOPE_ERROR';
    throw error;
  }
  if (!response?.data) {
    const error = new ApiError(`Missing area data for ${resourceId}`, { data: response });
    error.category = 'API_ENVELOPE_ERROR';
    throw error;
  }
  return withLegacyAreaId(response.data);
}

/**
 * Get the best-fitting area for a given bounding box
 * @param minLat - Minimum latitude
 * @param maxLat - Maximum latitude
 * @param minLon - Minimum longitude
 * @param maxLon - Maximum longitude
 * @returns Area information that best fits the provided bounds
 */
export async function bestFit(
  minLat: number,
  maxLat: number,
  minLon: number,
  maxLon: number,
  signal?: AbortSignal,
): Promise<AreaBestFitResponse> {
  const response = await apiClient<{ data: AreaBestFitResponse }>({
    path: '/v2/area/bestfit',
    queryParams: { minLat, maxLat, minLon, maxLon },
    signal,
  });
  return withLegacyAreaId(response.data);
}

/**
 * Fetches the area boundary straight from the Mapi CDN and re-encodes its
 * GeoJSON rings as polylines, so callers and the map draw it exactly like an
 * API boundary. Plain `fetch` on purpose — this is a public external CDN
 * (CORS `*`), not a hotpads-api endpoint, so it skips the apiClient proxy.
 */
async function getGeoJsonBoundary(geoJsonUrl: string): Promise<EncodedBoundaryResponse> {
  const response = await fetch(geoJsonUrl);
  if (!response.ok) {
    throw new ApiError(`GeoJSON boundary fetch failed for ${geoJsonUrl}`, { status: response.status });
  }
  const boundary = geoJsonFeatureToEncodedBoundary((await response.json()) as GeoJsonBoundaryFeature);
  if (!boundary) {
    throw new ApiError(`GeoJSON boundary at ${geoJsonUrl} has no polygon geometry`);
  }
  return { boundary };
}

function normalizeEncodedBoundaryResponse(data: EncodedBoundaryResponse): EncodedBoundaryResponse {
  if (!data.boundary || hasUsableEncodedBoundary(data.boundary.coordinates)) return data;
  return { ...data, boundary: undefined };
}

/**
 * Get encoded boundary coordinates for an area
 * @param areaId - Legacy area ID
 * @param scope - Typed Zillow scope usable without a legacy area ID
 * @returns Encoded boundary string that can be decoded to lat/lng coordinates
 */
export async function getEncodedBoundary(
  identityOrAreaId: AreaIdentity | string | undefined,
  suppliedScope?: HpSearchScopeDto,
): Promise<EncodedBoundaryResponse> {
  const identity =
    typeof identityOrAreaId === 'string'
      ? { legacyAreaId: identityOrAreaId, scope: suppliedScope }
      : { ...identityOrAreaId, scope: suppliedScope ?? toSearchScope(identityOrAreaId) };
  const legacyAreaId = identity?.legacyAreaId ?? identity?.id;
  const response = await apiClient<{ data: EncodedBoundaryResponse }>({
    path: '/v2/area/encodedBoundaryById',
    queryParams: identity?.scope
      ? { type: identity.scope.type, id: identity.scope.id, ...(legacyAreaId ? { areaId: legacyAreaId } : {}) }
      : { id: legacyAreaId },
  });
  const data = response.data;
  // Region-backed legacy-area responses can carry a Mapi CDN GeoJSON url;
  // prefer it over the inlined boundary. Any CDN failure falls back to a
  // usable response boundary; point-only regions have no boundary to draw.
  if (data?.boundaryGeoJsonUrl) {
    try {
      const { boundary } = await getGeoJsonBoundary(data.boundaryGeoJsonUrl);
      return normalizeEncodedBoundaryResponse({ ...data, boundary });
    } catch {
      return normalizeEncodedBoundaryResponse(data);
    }
  }
  return normalizeEncodedBoundaryResponse(data);
}

/**
 * Autocomplete search for areas (cities, neighborhoods, etc.)
 * @param partial - Partial search string
 * @param options - Optional parameters for filtering results
 * @returns Array of matching area results (API returns array directly, not wrapped)
 */
export async function autocomplete(
  partial: string,
  options?: {
    limit?: number;
    types?: Array<string>;
    lat?: number;
    lon?: number;
  },
): Promise<Array<AutocompleteResult>> {
  const types = options?.types || [
    'borough',
    'city',
    'neighborhood',
    'primaryschool',
    'middleschool',
    'highschool',
    'mixedschool',
    'elemschdist',
    'secschdist',
    'unifschdist',
    'zip',
    'address',
  ];

  const response = await apiClient<{ data: Array<AutocompleteResult> }>({
    path: '/v2/area/autocompleteV2',
    queryParams: {
      partial,
      limit: options?.limit || 6,
      types: types.join(','),
      ...(options?.lat && { lat: options.lat }),
      ...(options?.lon && { lon: options.lon }),
    },
  });
  return response.data;
}

// ============================================================================
// V3 Autocomplete (web/v3/area/autocomplete)
// ============================================================================

/** Top-level kind of a V3 autocomplete suggestion. */
export type AutocompleteSuggestionType = 'area' | 'listing';

/** Area subtype for `type: 'area'` suggestions (null for listings). */
export type AutocompleteAreaType = 'city' | 'neighborhood' | 'borough' | 'county' | 'zip' | 'school' | 'flexRegion';

/**
 * A single V3 autocomplete suggestion. Unlike V2's overloaded `type` field
 * (capitalized `City`/`Zip` but lowercase `address`), V3 splits the kind
 * (`type`) from the area subtype (`areaType`) and supplies a server-rendered
 * `label`, so callers no longer reconstruct display strings or pattern-match
 * on inconsistent casing.
 */
export type AutocompleteSuggestion = {
  type: AutocompleteSuggestionType;
  areaType?: AutocompleteAreaType | null;
  id?: string;
  /** Typed Zillow region id; the BE omits `id` on region suggestions once its area migration flag is on. */
  regionId?: number;
  label: string;
  resourceId?: string | null;
  lotIdEncoded?: string | null;
  city?: string | null;
  county?: string | null;
  state?: string | null;
};

/**
 * Shortest `partial` the endpoint accepts. Below this it answers 400
 * `invalidRequest` ("partial must be at least 3 characters"), so callers that
 * gate their UI on a minimum query length should use this rather than pick
 * their own and drift from the contract.
 */
export const AUTOCOMPLETE_MIN_PARTIAL_LENGTH = 3;

/**
 * V3 area + listing autocomplete. Returns the suggestions array directly
 * (empty on backend failure, or on a `partial` the endpoint would reject).
 * Prefer this over {@link autocomplete} (V2).
 */
export async function autocompleteV3(
  partial: string,
  options?: {
    limit?: number;
    types?: Array<string>;
    lat?: number;
    lon?: number;
    includeInactive?: boolean;
    signal?: AbortSignal;
  },
): Promise<Array<AutocompleteSuggestion>> {
  // Never spend a request on a query the endpoint is guaranteed to 400.
  if (partial.trim().length < AUTOCOMPLETE_MIN_PARTIAL_LENGTH) return [];

  const types = options?.types ?? ['city', 'neighborhood', 'borough', 'county', 'zip', 'school', 'address'];

  const response = await apiClient<{ data?: { suggestions?: Array<AutocompleteSuggestion> } }>({
    path: '/web/v3/area/autocomplete',
    queryParams: {
      partial,
      limit: options?.limit ?? 6,
      types: types.join(','),
      ...(options?.lat && { lat: options.lat }),
      ...(options?.lon && { lon: options.lon }),
      ...(options?.includeInactive && { includeInactive: options.includeInactive }),
    },
    signal: options?.signal,
  });
  return response.data?.suggestions ?? [];
}

// ============================================================================
// Area Facts Types
// ============================================================================

export type NearbyArea = {
  resourceId: string;
  name: string;
  type?: string;
};

export type NearbyAreasResponse = {
  areas: Array<NearbyArea>;
};

export type AreaArticle = HpAreaArticleDto;
type AreaArticleResponse = HpApiResponseDto<HpAreaArticleDto>;

// ============================================================================
// Area Article & Nearby Methods
// ============================================================================

export async function articleByScope(
  scope: HpSearchScopeDto,
  options?: AreaRequestOptions,
): Promise<AreaArticle | null> {
  if (scope.type !== HpSearchScopeType.ZILLOW_REGION) return null;
  const response = await apiClient<AreaArticleResponse>({
    path: '/web/v3/area/article',
    queryParams: {
      scope: JSON.stringify(scope),
    },
    timeout: options?.timeoutMs,
  });
  if (response.error) {
    const error = new ApiError(response.error.message, { data: response });
    error.category = response.error.reason === HpApiWebApiError.NOT_FOUND ? 'API_ENVELOPE_EMPTY' : 'API_ENVELOPE_ERROR';
    throw error;
  }
  if (!response.data) {
    const error = new ApiError('Empty data in /web/v3/area/article response', { data: response });
    error.category = 'API_ENVELOPE_EMPTY';
    throw error;
  }
  return response.data;
}

/**
 * Fetch nearby areas of a given type (city, neighborhood, etc.)
 */
export async function nearby(params: {
  areaResourceId: string;
  type?: string;
  limit?: number;
}): Promise<NearbyAreasResponse> {
  const response = await apiClient<{ data: NearbyAreasResponse }>({
    path: '/v2/area/nearby',
    queryParams: {
      type: params.type ?? 'city',
      areaResourceId: params.areaResourceId,
      limit: params.limit ?? 7,
    },
  });
  return response.data;
}

export interface ReverseGeocodeResponse {
  areas: Array<AreaResponse>;
}

/**
 * Reverse geocode lat/lon coordinates to hierarchical area list
 * Returns areas from most general (state) to most specific (neighborhood)
 * Typically: areas[0] = state, areas[1] = city, areas[2+] = neighborhoods
 *
 * @param lat - Latitude
 * @param lon - Longitude
 * @returns Hierarchical list of areas containing the coordinates
 */
export async function reverseGeocode(lat: number, lon: number): Promise<ReverseGeocodeResponse> {
  const response = await apiClient<{ data: ReverseGeocodeResponse }>({
    path: '/v2g/area/reverseGeocode',
    queryParams: { lat, lon },
  });
  return response.data;
}

// Area API namespace
export const area = {
  byIp,
  byResourceId,
  bestFit,
  getEncodedBoundary,
  autocomplete,
  autocompleteV3,
  reverseGeocode,
  articleByScope,
  nearby,
};
